import { formatMoneyCurrency } from '@/shared/utils/formatMoneyDisplay';
import { DetailList } from '../../DetailList';
import { REASON_LABEL, REQUESTING_PARTY_LABEL } from '../../../utils/cancellationCopy';
import type { CancelAgreementFormInput } from '../../../schemas/cancelAgreement.schema';
import type { CancelAction, CancellationReason, CancellationRequestingParty } from '../../../types/cancellation.types';

interface Props {
    values: CancelAgreementFormInput;
    action: Pick<CancelAction, 'estimate'>;
}

const CONSEQUENCES = [
    'The agreement moves to Pending Cancellation straight away.',
    'It becomes Cancelled once any payment still in progress has cleared.',
    'Direct Debit collections stop when it becomes Cancelled.',
    'Once started, the cancellation cannot be withdrawn.',
];

const labelFor = <T extends string>(labels: Record<T, string>, value: string): string | null =>
    value in labels ? labels[value as T] : null;

function estimatedRefund(estimate: CancelAction['estimate']): string | null {
    if (estimate === null) return null;
    return estimate.noCustomerRefund ? 'No refund' : formatMoneyCurrency(estimate.refund);
}

export function CancelAgreementSummary({ values, action }: Props) {
    return (
        <div className="space-y-4">
            <DetailList
                emptyMessage="No details entered."
                fields={[
                    { label: 'Reason', value: labelFor<CancellationReason>(REASON_LABEL, values.reason) },
                    {
                        label: 'Asked by',
                        value: labelFor<CancellationRequestingParty>(REQUESTING_PARTY_LABEL, values.requestingParty),
                    },
                    { label: 'Case reference', value: values.caseReference },
                    { label: 'Estimated refund', value: estimatedRefund(action.estimate) },
                    { label: 'Note', value: values.note },
                ]}
            />
            <ul className="list-disc space-y-1 pl-5 text-sm text-mp-foreground">
                {CONSEQUENCES.map((line) => (
                    <li key={line}>{line}</li>
                ))}
            </ul>
        </div>
    );
}
